import type { CreateOrganizationBody, IssuedLink, OrganizationEntry } from '@obras/shared';
import { formatBytes, formatDate, maskCnpj } from '@obras/shared';
import { Building2, Pencil, Plus, ScrollText } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';

import { PageHeader } from '../components/AppShell.js';
import { Button } from '../components/ui/Button.js';
import { Card, Kpi } from '../components/ui/Card.js';
import { SearchInput, TextField } from '../components/ui/Field.js';
import { EmptyState, ErrorState, FormError, Skeleton } from '../components/ui/Feedback.js';
import { LinkBox } from '../components/ui/LinkBox.js';
import { Modal } from '../components/ui/Modal.js';
import { OrganizationAuditModal } from '../features/plataforma/OrganizationAuditModal.js';
import { OrganizationFormModal } from '../features/plataforma/OrganizationFormModal.js';
import { useCreateOrganization, useOrganizations } from '../features/plataforma/api.js';
import { ApiError } from '../lib/api.js';
import { cn } from '../lib/cn.js';

const ICON = { strokeWidth: 1.5 } as const;

function CreateOrganizationModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateOrganization();

  const [name, setName] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [link, setLink] = useState<IssuedLink | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  function close(): void {
    setLink(null);
    setName('');
    setCnpj('');
    setOwnerName('');
    setOwnerEmail('');
    setFormError(null);
    setFieldErrors({});
    onClose();
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});

    const body: CreateOrganizationBody = {
      name,
      cnpj: cnpj.trim() === '' ? undefined : cnpj.replace(/\D/g, ''),
      ownerName,
      ownerEmail,
    };

    try {
      const result = await create.mutateAsync(body);
      setLink(result.link);
    } catch (error) {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors());
        setFormError(error.details ? 'Verifique os campos destacados.' : error.message);
      } else {
        setFormError('Não foi possível cadastrar a organização.');
      }
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={link ? 'Organização cadastrada' : 'Nova organização'}
      description={
        link
          ? 'Entregue o link ao responsável para ele definir a senha.'
          : 'O responsável informado entra como proprietário do órgão.'
      }
      footer={
        link ? undefined : (
          <>
            <Button variant="ghost" onClick={close} disabled={create.isPending}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              form="organization-form"
              type="submit"
              loading={create.isPending}
              icon={<Plus {...ICON} size={16} />}
            >
              Cadastrar
            </Button>
          </>
        )
      }
    >
      {link ? (
        <LinkBox link={link} onClose={close} />
      ) : (
        <form
          id="organization-form"
          onSubmit={(event) => void handleSubmit(event)}
          className="flex flex-col gap-4"
          noValidate
        >
          <FormError>{formError}</FormError>

          <TextField
            label="Nome da organização"
            required
            autoFocus
            placeholder="Prefeitura Municipal de…"
            value={name}
            onChange={(event) => setName(event.target.value)}
            {...(fieldErrors['name'] ? { error: fieldErrors['name'] } : {})}
          />

          <TextField
            label="CNPJ"
            mono
            /*
             * `inputMode="text"`, e não numérico: o CNPJ alfanumérico já é
             * realidade, e teclado só de números deixaria a letra inalcançável
             * no celular.
             */
            inputMode="text"
            placeholder="00.000.000/0000-00"
            value={cnpj}
            onChange={(event) => setCnpj(maskCnpj(event.target.value))}
            {...(fieldErrors['cnpj'] ? { error: fieldErrors['cnpj'] } : {})}
          />

          <TextField
            label="Nome do responsável"
            required
            hint="Recebe o papel de proprietário e cadastra os demais usuários."
            value={ownerName}
            onChange={(event) => setOwnerName(event.target.value)}
            {...(fieldErrors['ownerName'] ? { error: fieldErrors['ownerName'] } : {})}
          />

          <TextField
            label="E-mail do responsável"
            type="email"
            required
            value={ownerEmail}
            onChange={(event) => setOwnerEmail(event.target.value)}
            {...(fieldErrors['ownerEmail'] ? { error: fieldErrors['ownerEmail'] } : {})}
          />
        </form>
      )}
    </Modal>
  );
}

/**
 * Quanto do espaço contratado a organização já ocupa.
 *
 * A barra usa a cor de aviso a partir de 80% e a de perigo a partir de 95% —
 * antes disso é informação, depois é assunto. Estourado, mostra a barra cheia:
 * a conta pode passar do limite quando o limite encolhe, e barra maior que a
 * própria caixa não comunica nada.
 */
function Espaco({ usado, limite }: { usado: number; limite: number }) {
  const fracao = limite > 0 ? Math.min(1, usado / limite) : 0;
  const porcento = Math.round(fracao * 100);

  return (
    <div className="flex min-w-[132px] flex-col items-end gap-1">
      <span className="tabular text-[13px]">
        {formatBytes(usado)} <span className="text-ink-3">de {formatBytes(limite)}</span>
      </span>
      <span
        className="h-1 w-full overflow-hidden rounded-full bg-sunken"
        role="img"
        aria-label={`${porcento}% do espaco em uso`}
      >
        <span
          className={cn(
            'block h-full rounded-full',
            porcento >= 95 ? 'bg-danger' : porcento >= 80 ? 'bg-warning' : 'bg-ink',
          )}
          style={{ width: `${Math.max(fracao * 100, usado > 0 ? 2 : 0)}%` }}
        />
      </span>
    </div>
  );
}

export function PlataformaOrganizacoesPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editando, setEditando] = useState<OrganizationEntry | null>(null);
  const [trilhaDe, setTrilhaDe] = useState<OrganizationEntry | null>(null);

  const [busca, setBusca] = useState('');
  const [termo, setTermo] = useState('');

  /* Espera a digitacao parar antes de consultar, como nas demais listagens. */
  useEffect(() => {
    const timer = setTimeout(() => setTermo(busca), 300);
    return () => clearTimeout(timer);
  }, [busca]);

  const { data, isPending, isError, error, refetch } = useOrganizations(termo);

  const items = data?.items ?? [];
  const totalUsers = items.reduce((sum, item) => sum + item.usersCount, 0);
  const totalObras = items.reduce((sum, item) => sum + item.obrasCount, 0);

  return (
    <>
      <PageHeader
        title="Organizações"
        description="Os órgãos que usam o sistema."
        action={
          <Button
            variant="primary"
            icon={<Plus {...ICON} size={16} />}
            onClick={() => setCreateOpen(true)}
          >
            Nova organização
          </Button>
        }
      />

      <div className="mb-4 flex justify-end">
        <div className="relative w-full sm:w-80">
          <SearchInput
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
            onClear={() => setBusca('')}
            placeholder="Buscar por nome, apelido ou CNPJ"
            aria-label="Buscar organizacoes"
          />
        </div>
      </div>

      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : 'Erro desconhecido.'}
          onRetry={() => void refetch()}
        />
      ) : isPending ? (
        <Skeleton className="h-80 rounded-lg" />
      ) : (
        <div className="flex flex-col gap-6">
          <div className="grid gap-3 sm:grid-cols-3">
            <Kpi label="Organizações" value={String(items.length)} />
            <Kpi label="Usuários" value={String(totalUsers)} />
            <Kpi label="Obras" value={String(totalObras)} tone="ink" />
          </div>

          {items.length === 0 ? (
            <Card square>
              <EmptyState
                icon={Building2}
                title={termo ? 'Nenhuma organização encontrada' : 'Nenhuma organização'}
                description={
                  termo
                    ? 'Nenhum órgão com esse nome, apelido ou CNPJ.'
                    : 'Cadastre o primeiro órgão para começar.'
                }
                action={
                  termo ? undefined : (
                    <Button variant="primary" onClick={() => setCreateOpen(true)}>
                      Nova organização
                    </Button>
                  )
                }
              />
            </Card>
          ) : (
            <Card square className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left">
                <caption className="sr-only">Organizações cadastradas</caption>
                <thead className="bg-sunken">
                  <tr className="text-[11px] font-medium tracking-[0.04em] text-ink-3 uppercase *:border-b-2 *:border-line-2">
                    <th scope="col" className="px-5 py-3">
                      Organização
                    </th>
                    <th scope="col" className="px-5 py-3 text-right">
                      Usuários
                    </th>
                    <th scope="col" className="px-5 py-3 text-right">
                      Obras
                    </th>
                    <th scope="col" className="px-5 py-3 text-right">
                      Espaço em nuvem
                    </th>
                    <th scope="col" className="px-5 py-3 text-right">
                      Criada em
                    </th>
                    <th scope="col" className="px-5 py-3" />
                  </tr>
                </thead>

                <tbody>
                  {items.map((organization) => (
                    <tr key={organization.id} className="border-t border-line">
                      <td className="px-5 py-3">
                        <p className="text-[14px] font-medium">{organization.name}</p>
                        <p className="font-mono text-[11px] text-ink-3">
                          {organization.slug}
                          {organization.cnpj ? ` · ${organization.cnpj}` : ''}
                        </p>
                      </td>
                      <td className="tabular px-5 py-3 text-right text-[13px]">
                        {organization.usersCount}
                      </td>
                      <td className="tabular px-5 py-3 text-right text-[13px]">
                        {organization.obrasCount}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end">
                          <Espaco
                            usado={organization.storageUsedBytes}
                            limite={organization.storageLimitBytes}
                          />
                        </div>
                      </td>
                      <td className="tabular px-5 py-3 text-right font-mono text-[11px] text-ink-3">
                        {formatDate(organization.createdAt)}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="size-8"
                            title={`Trilha de ${organization.name}`}
                            aria-label={`Trilha de ${organization.name}`}
                            onClick={() => setTrilhaDe(organization)}
                          >
                            <ScrollText {...ICON} size={16} aria-hidden />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="size-8"
                            title={`Editar ${organization.name}`}
                            aria-label={`Editar ${organization.name}`}
                            onClick={() => setEditando(organization)}
                          >
                            <Pencil {...ICON} size={16} aria-hidden />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}

      <CreateOrganizationModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <OrganizationFormModal organization={editando} onClose={() => setEditando(null)} />
      <OrganizationAuditModal organization={trilhaDe} onClose={() => setTrilhaDe(null)} />
    </>
  );
}
